import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useDocumentTags } from '@/hooks/react-query/useDocumentTags';

interface DocumentTagBadgeProps {
  tagName: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
  onClick?: () => void;
}

export function DocumentTagBadge({
  tagName,
  size = 'md',
  showIcon = false,
  className,
  onClick,
}: DocumentTagBadgeProps) {
  const { data: tagsResponse } = useDocumentTags();
  const tagData = tagsResponse?.data?.find((tag) => tag.name === tagName);

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0',
    md: 'text-sm px-2 py-0.5',
    lg: 'text-base px-2.5 py-1',
  };

  const baseColor = tagData?.color || '#3B82F6';

  return (
    <Badge
      variant="secondary"
      style={{
        backgroundColor: baseColor + '20',
        borderColor: baseColor,
        color: baseColor,
      }}
      className={cn(
        sizeClasses[size],
        onClick && 'cursor-pointer hover:opacity-80',
        className
      )}
      onClick={onClick}
    >
      {showIcon && tagData?.icon && (
        <span className="mr-1">{tagData.icon}</span>
      )}
      {tagName}
    </Badge>
  );
}

interface DocumentTagsListProps {
  tags: string[];
  size?: 'sm' | 'md' | 'lg';
  maxDisplay?: number;
  onTagClick?: (tag: string) => void;
  className?: string;
}

export function DocumentTagsList({
  tags,
  size = 'sm',
  maxDisplay = 3,
  onTagClick,
  className,
}: DocumentTagsListProps) {
  if (!tags || tags.length === 0) {
    return null;
  }

  const displayTags = tags.slice(0, maxDisplay);
  const remainingCount = tags.length - maxDisplay;

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {displayTags.map((tag) => (
        <DocumentTagBadge
          key={tag}
          tagName={tag}
          size={size}
          onClick={onTagClick ? () => onTagClick(tag) : undefined}
        />
      ))}
      {remainingCount > 0 && (
        <Badge
          variant="outline"
          className={cn('text-muted-foreground', size === 'sm' && 'text-xs')}
        >
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
}
